import React, { useEffect, useRef, useState } from 'react';

const PINK = '#7b1f40';
const MEDIA: Record<string, { src: string; poster?: string; label: string }> = {
  'tour-welcome.mp4': { src: '/tour/refreshed/welcome.mp4', poster: '/tour/refreshed/welcome.jpg', label: 'Welcome to AdMoji animation' },
  'tour-01-brand.mp4': { src: '/tour/refreshed/brand.mp4', poster: '/tour/refreshed/brand.jpg', label: 'Add and review your brand demonstration' },
  'tour-attach-brand.mp4': { src: '/tour/refreshed/attach-brand.mp4', poster: '/tour/refreshed/attach-brand.jpg', label: 'Choose your brand and attach references demonstration' },
  'tour-03-prompt.mp4': { src: '/tour/refreshed/prompt.mp4', poster: '/tour/refreshed/prompt.jpg', label: 'Write a creative brief demonstration' },
  'tour-04-dials.mp4': { src: '/tour/refreshed/dials.mp4', poster: '/tour/refreshed/dials.jpg', label: 'Choose ad sizes and image settings demonstration' },
  'tour-05-discover.mp4': { src: '/tour/refreshed/discover.mp4', poster: '/tour/refreshed/discover.jpg', label: 'Browse Inspiration demonstration' },
};

function TourVideo({ media, wide }: { media: (typeof MEDIA)[string]; wide?: boolean }) {
  const video = useRef<HTMLVideoElement>(null);
  const [failed, setFailed] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => {
      setReducedMotion(preference.matches);
      if (preference.matches) video.current?.pause();
      else void video.current?.play().catch(() => { /* Leave the poster visible if autoplay is unavailable. */ });
    };
    sync();
    preference.addEventListener('change', sync);
    return () => preference.removeEventListener('change', sync);
  }, []);

  return (
    <div data-testid="tour-video-slot" style={{ position: 'relative', aspectRatio: wide ? '16 / 9' : '4 / 3', borderRadius: 8, overflow: 'hidden', background: '#F7F9FC', marginBottom: 12 }} onKeyDown={(event) => event.stopPropagation()}>
      {failed ? (
        <div style={{ position: 'relative', height: '100%' }}>
          {media.poster && <img src={media.poster} alt={media.label} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />}
          <div role="status" style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '8px 12px', background: 'rgba(255,255,255,.96)', textAlign: 'center', fontSize: 12, color: '#334155' }}>
            Video unavailable. You can still follow the tour below.
          </div>
        </div>
      ) : (
        <video ref={video} data-testid="tour-video" aria-label={media.label} src={media.src} poster={media.poster} muted loop playsInline disablePictureInPicture disableRemotePlayback tabIndex={-1} preload="metadata" onError={() => setFailed(true)} style={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }} />
      )}
      {!failed && reducedMotion && media.poster && <img data-testid="tour-reduced-motion-poster" src={media.poster} alt={media.label} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', background: '#F7F9FC' }} />}
    </div>
  );
}

export function TourVideoSlot({ file, wide }: { file?: string; wide?: boolean }) {
  if (!file) return null;
  const media = MEDIA[file];
  if (media) return <TourVideo key={file} media={media} wide={wide} />;
  return (
    <div data-testid="tour-video-slot" style={{ aspectRatio: wide ? '16 / 9' : '4 / 3', borderRadius: 8, border: `1.5px dashed ${PINK}`, background: 'rgba(123,31,64,0.08)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, padding: 8, marginBottom: 12 }}>
      <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', color: PINK }}>Video</span>
      <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11.5, color: '#161616' }}>{file}</span>
    </div>
  );
}
